import React, { memo } from 'react';
import { CTASection } from '../../components/CTASection';
import { Gift, ExternalLink, Sparkles, Award, Users, TrendingUp } from 'lucide-react';
export function PromotionalProducts() {
  const benefits = [{
    icon: <Award className="w-6 h-6" data-id="element-1836" />,
    title: 'Brand Visibility',
    description: 'Keep your brand top-of-mind with useful, high-quality promotional items'
  }, {
    icon: <Users className="w-6 h-6" data-id="element-1837" />,
    title: 'Customer Loyalty',
    description: 'Build lasting relationships with thoughtful, branded gifts'
  }, {
    icon: <TrendingUp className="w-6 h-6" data-id="element-1838" />,
    title: 'Marketing ROI',
    description: 'Cost-effective marketing that delivers long-term brand exposure'
  }];
  const categories = [{
    icon: '☕',
    name: 'Drinkware',
    description: 'Mugs, tumblers, water bottles'
  }, {
    icon: '🎒',
    name: 'Bags & Totes',
    description: 'Backpacks, totes, laptop bags'
  }, {
    icon: '✏️',
    name: 'Office Supplies',
    description: 'Pens, notebooks, desk accessories'
  }, {
    icon: '👕',
    name: 'Apparel',
    description: 'T-shirts, polos, jackets, hats'
  }, {
    icon: '💻',
    name: 'Tech Accessories',
    description: 'USB drives, chargers, phone accessories'
  }, {
    icon: '🏆',
    name: 'Awards & Recognition',
    description: 'Plaques, trophies, certificates'
  }];
  return <div className="flex flex-col w-full" data-id="element-1839">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-20" data-id="element-1840">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1841">
          <div className="flex justify-center mb-6" data-id="element-1842">
            <Gift className="w-16 h-16 text-white" data-id="element-1843" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1844">
            Promotional Products
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-1845">
            Make a lasting impression with custom branded promotional products.
            From trade shows to client gifts, we help you find the perfect items
            to showcase your brand.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-1846">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1847">
          <div className="max-w-4xl mx-auto mb-16" data-id="element-1848">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1849">
              Elevate Your Brand with Promotional Products
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-1850">
              <p data-id="element-1851">
                At Wear Haus Designs, we understand the power of a well-chosen
                promotional product. Whether you're looking to make a splash at
                your next trade show, reward loyal customers, or create
                memorable corporate gifts, the right promotional items can leave
                a lasting impression that keeps your brand top-of-mind long
                after the initial interaction.
              </p>
              <p data-id="element-1852">
                From classic drinkware and apparel to cutting-edge tech
                accessories and unique custom items, we offer access to
                thousands of promotional products that can be customized with
                your logo and branding. Our team works with you to select items
                that align with your brand identity, budget, and marketing
                goals—then we handle all the customization using our in-house
                printing, embroidery, and laser etching services to ensure
                exceptional quality.
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-1853">
            {benefits.map((benefit, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100 text-center" data-id="element-1854">
                <div className="w-12 h-12 bg-wh-pink rounded-lg flex items-center justify-center text-white mx-auto mb-4" data-id="element-1855">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1856">
                  {benefit.title}
                </h3>
                <p className="text-gray-600" data-id="element-1857">{benefit.description}</p>
              </div>)}
          </div>

          {/* Large CTA to Koozie Group */}
          <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-12 mb-16 text-center text-white" data-id="element-1858">
            <Sparkles className="w-16 h-16 mx-auto mb-6" data-id="element-1859" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1860">
              Browse Thousands of Promotional Products
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1861">
              Explore our extensive catalog of promotional items—from drinkware
              and apparel to tech accessories and unique gifts. Find the perfect
              products to represent your brand!
            </p>
            <a href="https://www.kooziegroup.com/US/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-white text-wh-pink font-bold text-lg rounded-lg hover:bg-gray-100 transition-colors shadow-lg" data-id="element-1862">
              View Product Catalog
              <ExternalLink className="w-6 h-6" data-id="element-1863" />
            </a>
            <p className="text-sm text-white/80 mt-4" data-id="element-1864">Opens in a new window</p>
          </div>

          {/* Popular Categories */}
          <div className="mb-16" data-id="element-1865">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1866">
              Popular Product Categories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1867">
              {categories.map((category, index) => <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-wh-teal transition-colors" data-id="element-1868">
                  <div className="text-4xl mb-3" data-id="element-1869">{category.icon}</div>
                  <h3 className="text-xl font-bold text-wh-teal mb-2" data-id="element-1870">
                    {category.name}
                  </h3>
                  <p className="text-gray-600" data-id="element-1871">{category.description}</p>
                </div>)}
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-12 text-white mb-16" data-id="element-1872">
            <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-1873">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-1874">
              <div className="text-center" data-id="element-1875">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-wh-teal text-2xl font-bold mx-auto mb-4" data-id="element-1876">
                  1
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1877">Browse</h3>
                <p className="text-white/90" data-id="element-1878">
                  Explore thousands of promotional products in our catalog
                </p>
              </div>
              <div className="text-center" data-id="element-1879">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-wh-teal text-2xl font-bold mx-auto mb-4" data-id="element-1880">
                  2
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1881">Select</h3>
                <p className="text-white/90" data-id="element-1882">
                  Choose items that align with your brand and budget
                </p>
              </div>
              <div className="text-center" data-id="element-1883">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-wh-teal text-2xl font-bold mx-auto mb-4" data-id="element-1884">
                  3
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1885">Customize</h3>
                <p className="text-white/90" data-id="element-1886">
                  We add your logo using our in-house services
                </p>
              </div>
              <div className="text-center" data-id="element-1887">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-wh-teal text-2xl font-bold mx-auto mb-4" data-id="element-1888">
                  4
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1889">Deliver</h3>
                <p className="text-white/90" data-id="element-1890">
                  Receive your branded promotional products ready to impress
                </p>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-white rounded-2xl p-12 text-center border-2 border-wh-yellow" data-id="element-1891">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1892">
              Ready to Promote Your Brand?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-1893">
              Let's find the perfect promotional products for your next
              campaign, event, or client gift. Our team is here to help you make
              the right choice.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1894">
              <a href="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-wh-pink text-white font-bold rounded-lg hover:bg-pink-600 transition-colors" data-id="element-1895">
                Get a Quote
              </a>
              <a href="https://www.kooziegroup.com/US/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-wh-yellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-1896">
                Browse Products
                <ExternalLink className="w-5 h-5" data-id="element-1897" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-1898" />
    </div>;
}